import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n";
import { Button } from "@/components/ui/button";
import { 
  GraduationCap, 
  BookOpen, 
  Clock, 
  Trophy, 
  CheckCircle, 
  ArrowRight,
  Landmark,
  MapPin,
  Scale,
  Flag,
  Users,
  Building
} from "lucide-react";

const Landing = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const features = [
    { icon: BookOpen, title: t("landing_feature1_title"), description: t("landing_feature1_desc") },
    { icon: Clock, title: t("landing_feature2_title"), description: t("landing_feature2_desc") },
    { icon: Trophy, title: t("landing_feature3_title"), description: t("landing_feature3_desc") }
  ];

  const categories = [
    { icon: Landmark, name: "Histoire de France", color: "bg-amber-500" },
    { icon: MapPin, name: "Géographie", color: "bg-emerald-500" },
    { icon: Scale, name: "Valeurs républicaines", color: "bg-blue-500" },
    { icon: Building, name: "Institutions", color: "bg-purple-500" },
    { icon: Flag, name: "Symboles nationaux", color: "bg-red-500" },
    { icon: Users, name: "Culture et patrimoine", color: "bg-pink-500" },
    { icon: Landmark, name: "Union Européenne", color: "bg-indigo-500" },
    { icon: Scale, name: "Droits et devoirs", color: "bg-cyan-500" },
    { icon: Users, name: "Vie en société", color: "bg-orange-500" },
    { icon: Building, name: "Démarches administratives", color: "bg-teal-500" }
  ];

  const stats = [
    { value: "134", label: t("landing_stat_questions") },
    { value: "10", label: t("landing_stat_categories") },
    { value: "30", label: t("landing_stat_fiches") }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="landing-hero relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm mb-6">
                <GraduationCap className="h-4 w-4" />
                <span>{t("landing_title")}</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-6">
                {t("landing_title")}
              </h1>
              <p className="text-lg md:text-xl text-blue-100 mb-8 leading-relaxed">
                {t("landing_subtitle")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                {user ? (
                  <Button 
                    size="lg"
                    onClick={() => navigate("/dashboard")}
                    className="bg-white text-blue-900 hover:bg-blue-50 font-semibold px-8"
                    data-testid="go-to-dashboard-btn"
                  >
                    {t("nav_dashboard")}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                ) : (
                  <>
                    <Button 
                      size="lg"
                      onClick={() => navigate("/auth")}
                      className="bg-white text-blue-900 hover:bg-blue-50 font-semibold px-8"
                      data-testid="get-started-btn"
                    >
                      {t("landing_cta")}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button 
                      size="lg"
                      variant="outline"
                      onClick={() => navigate("/auth?mode=login")}
                      className="border-white/30 text-white hover:bg-white/10"
                      data-testid="login-hero-btn"
                    >
                      J'ai déjà un compte
                    </Button>
                  </>
                )}
              </div>
            </div>
            <div className="hidden md:block">
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1769092992447-18050cf9bd26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwdW5pdmVyc2l0eSUyMHN0dWRlbnRzJTIwc3R1ZHlpbmclMjBncm91cCUyMGxpYnJhcnl8ZW58MHx8fHwxNzczMTMxMzM5fDA&ixlib=rb-4.1.0&q=85"
                  alt="Étudiants préparant le test civique"
                  className="rounded-2xl shadow-2xl w-full max-w-md mx-auto"
                />
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">100% Gratuit</p>
                      <p className="text-sm text-slate-500">Accès illimité</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-blue-900">{stat.value}</p>
                <p className="text-slate-600 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-4">
              Tout pour réussir votre test
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Des outils modernes et efficaces pour vous préparer au test civique français.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="feature-card animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="h-14 w-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="h-7 w-7 text-blue-900" />
                </div>
                <h3 className="text-xl font-serif font-semibold text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-4">
              6 domaines de connaissance
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Maîtrisez tous les sujets abordés lors du test de naturalisation.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {categories.map((category, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 p-4 md:p-6 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <div className={`h-12 w-12 ${category.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <category.icon className="h-6 w-6 text-white" />
                </div>
                <span className="font-medium text-slate-900">{category.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 hero-gradient">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">
            {t("landing_ready")}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {t("landing_ready_sub")}
          </p>
          {!user && (
            <Button 
              size="lg"
              onClick={() => navigate("/auth")}
              className="bg-white text-blue-900 hover:bg-blue-50 font-semibold px-10"
              data-testid="cta-signup-btn"
            >
              {t("landing_cta_signup")}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-6 w-6 text-white" />
              <span className="font-serif font-bold text-lg text-white">CiviquePrep France</span>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/donation")}
                className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1"
                data-testid="footer-donation-link"
              >
                {t("landing_footer_support")}
              </button>
              <p className="text-sm">
                Plateforme de préparation au test civique
              </p>
            </div>
          </div>
          <div className="tricolore-stripe mt-8 rounded"></div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
